import type { StateCreator } from 'zustand';
import { setFileThumbnailBlob, clearFileThumbnails } from '../../db/fileThumbnails';
import type FilesThumbnailsSlice from '../../interfaces/FilesThumbnailsSlice';
import type { FileThumbnailCacheEntry } from '../../interfaces/FilesThumbnailsSlice';
import * as aes from '../../lib/aes';
import { FunctionService } from '../../services/functionService';

const functionService = new FunctionService();

/**
 * Decrypts an encrypted thumbnail blob using AES CBC.
 * Thumbnails are always JPEG images, so we set the type accordingly.
 */
async function decryptThumbnailBlob(blob: Blob): Promise<Blob> {
  try {
    // WebAssembly crypto module is hydrated from environment
    const wasmBytesString = import.meta.env.VITE_APP_WASM_MODULE_BYTES as string;
    const wasmBytes = wasmBytesString.split(',').map(Number);
    // Spin up WASM instance and get buffers/keys
    const { instance } = await WebAssembly.instantiate(new Uint8Array(wasmBytes));
    const memory = new Uint8Array((instance.exports as any).memory.buffer);
    const _x = (instance.exports as any).x();
    const _y = (instance.exports as any).y();
    const x = memory.subarray(_x, _x + 32);
    const y = memory.subarray(_y, _y + 16);
    const aesCbc = new aes.ModeOfOperation.cbc(x, y);

    // Transform blob to Uint8Array and decrypt
    const fileArrayBuffer = await new Response(blob).arrayBuffer();
    const encryptedBytes = new Uint8Array(fileArrayBuffer);
    const bytesToRemove = encryptedBytes[encryptedBytes.length - 1]!;
    // Remove padding indicator byte and decrypt
    const decryptedBytes = aesCbc.decrypt(encryptedBytes.slice(0, -1));
    // Remove PKCS padding for final blob
    // Thumbnails are always JPEG images
    const decrypted = new Blob([decryptedBytes.slice(0, bytesToRemove * -1)], {
      type: 'image/jpeg',
    });
    return decrypted;
  } catch (e) {
    console.error('Failed to decrypt thumbnail blob:', e);
    throw e;
  }
}

type WithMW = [];

export const createFilesThumbnailsSlice: StateCreator<
  FilesThumbnailsSlice,
  WithMW,
  [],
  FilesThumbnailsSlice
> = (set, get, _store) => ({
  filesThumbnails: {},
  fileThumbnailSkipUntil: {},
  /** Max thumbnails in memory; oldest (by expiresAt) are evicted. Persisted entries (token+expiresAt) survive refresh; blob URLs are recreated from token or refetched if token expired. */
  _maxFilesThumbnails: 500,

  /**
   * Sets a file thumbnail in the cache.
   * If the cache exceeds the maximum limit, removes the oldest entries (FIFO).
   *
   * @param fileId - The file ID
   * @param entry - The thumbnail cache entry
   */
  setFileThumbnail: (fileId: number, entry: FileThumbnailCacheEntry) => {
    set((state) => {
      const currentFilesThumbnails = state.filesThumbnails || {};
      const newFilesThumbnails = { ...currentFilesThumbnails, [fileId]: entry };

      // If we exceed the limit, remove oldest entries (those with earliest expiresAt)
      const entries = Object.entries(newFilesThumbnails);
      if (entries.length > state._maxFilesThumbnails) {
        // Sort by expiresAt (oldest first) and keep only the newest ones
        const sorted = entries.sort((a, b) => a[1].expiresAt - b[1].expiresAt);
        const toKeep = sorted.slice(-state._maxFilesThumbnails);
        const trimmed: Record<number, FileThumbnailCacheEntry> = {};
        toKeep.forEach(([id, entry]) => {
          trimmed[Number(id)] = entry;
        });

        // Revoke object URLs for removed thumbnails
        entries.forEach(([id, e]) => {
          if (!trimmed[Number(id)]) {
            URL.revokeObjectURL(e.fileThumbnailUrl);
          }
        });

        return {
          ...state,
          filesThumbnails: trimmed,
        };
      }

      return {
        ...state,
        filesThumbnails: newFilesThumbnails,
      };
    });
  },

  /**
   * Gets a file thumbnail from the cache.
   *
   * @param fileId - The file ID
   * @returns The thumbnail cache entry or undefined if not found
   */
  getFileThumbnail: (fileId: number) => {
    return get().filesThumbnails[fileId];
  },

  /**
   * Removes a specific file thumbnail from the cache and revokes its object URL.
   *
   * @param fileId - The file ID
   */
  clearFileThumbnail: (fileId: number) => {
    set((state) => {
      const currentFilesThumbnails = state.filesThumbnails || {};
      const entry = currentFilesThumbnails[fileId];

      // Revoke object URL before removing
      if (entry) {
        URL.revokeObjectURL(entry.fileThumbnailUrl);
      }

      const { [fileId]: removed, ...rest } = currentFilesThumbnails;

      return {
        ...state,
        filesThumbnails: rest,
      };
    });
  },

  /**
   * Removes all file thumbnails from the cache, clears skip-until timestamps, and revokes all object URLs.
   * Use on logout so persisted thumbnail data is cleared for the next user.
   */
  clearAllFilesThumbnails: () => {
    clearFileThumbnails();
    set((state) => {
      const currentFilesThumbnails = state.filesThumbnails || {};

      // Revoke all object URLs
      Object.values(currentFilesThumbnails).forEach((entry) => {
        URL.revokeObjectURL(entry.fileThumbnailUrl);
      });

      return {
        ...state,
        filesThumbnails: {},
        fileThumbnailSkipUntil: {},
      };
    });
  },

  /**
   * Checks if a file thumbnail is still valid (exists and hasn't expired).
   *
   * @param fileId - The file ID
   * @returns true if the thumbnail exists and is still valid
   */
  isFileThumbnailValid: (fileId: number) => {
    const entry = get().filesThumbnails[fileId];
    if (!entry) {
      return false;
    }
    // Check if expiration time is in the future
    return entry.expiresAt > Date.now();
  },

  /**
   * Sets the timestamp until which to skip file thumbnail fetch for this file (e.g. after error or empty response).
   */
  setFileThumbnailSkipUntil: (fileId: number, skipUntil: number) => {
    set((state) => ({
      ...state,
      fileThumbnailSkipUntil: { ...(state.fileThumbnailSkipUntil || {}), [fileId]: skipUntil },
    }));
  },

  /**
   * Clears skipUntil for a single file (e.g. on 401 so we refetch after login).
   */
  clearFileThumbnailSkipForFile: (fileId: number) => {
    set((state) => {
      const currentSkip = state.fileThumbnailSkipUntil || {};
      const { [fileId]: _removed, ...restSkip } = currentSkip;
      return { ...state, fileThumbnailSkipUntil: restSkip };
    });
  },

  /**
   * Clears all skipUntil timestamps (e.g. on login so stale 401 skip from previous session is wiped).
   */
  clearAllFileThumbnailSkipUntil: () => {
    set((state) => ({ ...state, fileThumbnailSkipUntil: {} }));
  },

  /**
   * Returns skipUntil for the file only if it is still in the future; otherwise undefined.
   */
  getFileThumbnailSkipUntil: (fileId: number) => {
    const skipUntil = get().fileThumbnailSkipUntil?.[fileId];
    if (skipUntil == null) return undefined;
    return skipUntil > Date.now() ? skipUntil : undefined;
  },

  /**
   * Restores a file thumbnail from a persisted token.
   * Fetches the encrypted blob using the token, decrypts it, and creates a new object URL.
   * This allows us to skip the getFileForViewer API call when we have a valid persisted token.
   *
   * @param fileId - The file ID
   * @param token - The JWT token to use for fetching the blob
   * @param expiresAt - The expiration timestamp in milliseconds
   */
  restoreFileThumbnailFromToken: async (fileId: number, token: string, expiresAt: number) => {
    try {
      // Step 1: Get encrypted blob from fileviewer using the persisted token
      const encryptedBlob = await functionService.getFileBlob(token);

      // Verify blob is valid
      if (!encryptedBlob || encryptedBlob.size === 0) {
        throw new Error('Received empty or invalid blob');
      }

      // Step 2: Decrypt the blob
      const decryptedBlob = await decryptThumbnailBlob(encryptedBlob);

      // Persist decrypted blob to IndexedDB for faster restore on next load
      setFileThumbnailBlob(fileId, decryptedBlob).catch(() => {});

      // Step 3: Create object URL from decrypted blob
      const objectUrl = URL.createObjectURL(decryptedBlob);

      // Step 4: Save to cache
      set((state) => {
        const currentFilesThumbnails = state.filesThumbnails || {};
        return {
          ...state,
          filesThumbnails: {
            ...currentFilesThumbnails,
            [fileId]: {
              fileThumbnailUrl: objectUrl,
              expiresAt,
              token,
            },
          },
        };
      });
    } catch (err) {
      console.error(`Failed to restore file thumbnail from token for file ${fileId}:`, err);
      // If restoration fails (e.g. 401 token expired), clear cache and skip so we refetch with fresh token
      set((state) => {
        const currentFilesThumbnails = state.filesThumbnails || {};
        const { [fileId]: removed, ...rest } = currentFilesThumbnails;
        const currentSkip = state.fileThumbnailSkipUntil || {};
        const { [fileId]: _removedSkip, ...restSkip } = currentSkip;
        return {
          ...state,
          filesThumbnails: rest,
          fileThumbnailSkipUntil: restSkip,
        };
      });
    }
  },
});
